'use client';

import { BreadcrumbLinkSlot, BreadcrumbPageSlot } from '@magicschool/spellbook/components/Breadcrumb/slots';
import { Icon } from '@magicschool/spellbook/components/Icon';
import { useOrderedSlots } from '@magicschool/spellbook/hooks/useOrderedSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { Fragment } from 'react';

export type BreadcrumbProps = React.ComponentProps<'nav'> & VariantProps<typeof breadcrumbVariants>;

const breadcrumbVariants = cva('min-w-0', {
  variants: {
    /** The visual appearance of the breadcrumb. */
    variant: {
      default: '',
      primary: '',
    },
  },
});

const BreadcrumbBase: React.FC<BreadcrumbProps> = ({ className, children, variant = 'default', ...props }) => {
  const { orderedSlots } = useOrderedSlots(children, {
    link: { Component: BreadcrumbLinkSlot, props: { variant } },
    page: { Component: BreadcrumbPageSlot, props: { variant } },
  });

  return (
    <nav data-spellbook-name="breadcrumb" className={cn(breadcrumbVariants({ variant }), className)} {...props}>
      <ol
        data-spellbook-name="breadcrumb-list"
        className={cn('text-muted-foreground flex flex-nowrap items-center min-w-0 w-full space-x-2', className)}
      >
        {orderedSlots.map((slot) => (
          <Fragment key={`item-${slot.originalIndex}`}>
            <li className={cn('flex items-center', slot.originalIndex === 0 ? 'shrink-0' : 'min-w-0 overflow-hidden')}>{slot.component}</li>
            {slot.originalIndex < orderedSlots.length - 1 && (
              <li className={cn('flex items-center shrink-0')}>
                <Icon name="chevron-right" role="presentation" className="rtl:rotate-180" color="default" size="sm" />
              </li>
            )}
          </Fragment>
        ))}
      </ol>
    </nav>
  );
};

/**
 * Displays a navigation path hierarchy showing the user's current location within the app.
 *
 * ### When to use
 * - To show the hierarchical path from a root page to the current page
 * - When users need a way to navigate back to parent sections
 * - In page headers to provide location context (typically via `PageHeader` breadcrumbs)
 *
 * ### When NOT to use
 * - For sequential step progress: use a stepper or progress indicator instead
 * - For tab-style navigation between sibling pages: use `Tabs` instead
 * - For primary site navigation: use `NavLink` in a sidebar instead
 *
 * ### API
 * - `variant`: Visual style of the breadcrumb (`'default'`, `'primary'`)
 *
 * ### Composition (slots)
 * - `<Breadcrumb.Link>`: A navigable link in the hierarchy. Accepts all `LinkProps` including `href`, `disabled`, and `asChild`. Must be included explicitly.
 * - `<Breadcrumb.Page>`: The current (final) page in the hierarchy. Rendered as non-interactive text with `aria-current="page"`. Must be included explicitly and placed last.
 *
 * ### Accessibility
 * - Renders as a `<nav>` > `<ol>` > `<li>` structure for proper semantic navigation
 * - `<Breadcrumb.Page>` automatically sets `aria-current="page"` and `aria-disabled="true"`
 * - Separator chevrons use `role="presentation"` to be ignored by screen readers
 */
export const Breadcrumb = Object.assign(BreadcrumbBase, {
  Link: BreadcrumbLinkSlot,
  Page: BreadcrumbPageSlot,
});

Breadcrumb.displayName = 'Breadcrumb';
